// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FormattedTimeValue } from '@/components/formatted-time-value';
import { ItemTriage } from '@/items/api/triage-types';
import { TriageExternalSystemIcon } from '@/items/components/triage-external-system-icon';
import {
  Box,
  Button,
  Chip,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { Link } from '@tanstack/react-router';

interface TriageTableRowProps {
  item: ItemTriage;
  onClassifyClick: (item: ItemTriage) => void;
  onDismissClick: (item: ItemTriage) => void;
}

/**
 * Table row component for displaying a single triage item.
 */
export function TriageTableRow(props: TriageTableRowProps) {
  const { item, onClassifyClick, onDismissClick } = props;

  // Determine the status chip color and label
  const isMatched = item.isTriaged;
  const isDismissed = item.isDismissed;

  const getStatusChip = () => {
    if (isDismissed) {
      return { color: 'default' as const, label: 'Dismissed' };
    }
    if (isMatched) {
      return { color: 'success' as const, label: 'Matched' };
    }
    return { color: 'warning' as const, label: 'Needs review' };
  };

  const statusChip = getStatusChip();

  // Determine the item type label
  const getItemTypeLabel = (): string | null => {
    if (!item.item) return null;
    if (!item.item.isPhysical) return 'Service';
    return item.item.hasAssets ? 'Device' : 'Bulk';
  };

  // Determine the action based on status
  const renderAction = () => {
    if (isMatched) {
      // Navigate to item detail page
      if (item.item) {
        return (
          <Link
            to="/catalog/items/$itemKey"
            params={{ itemKey: item.item.key }}
          >
            <Button size="small" variant="text" sx={{ whiteSpace: 'nowrap' }}>
              View {item.item.sku}
            </Button>
          </Link>
        );
      }
      return null;
    }

    // Dismissed items can still be classified
    if (isDismissed) {
      return (
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={() => onClassifyClick(item)}
        >
          Classify
        </Button>
      );
    }

    // Show classify and dismiss buttons for items needing review
    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          size="small"
          variant="text"
          color="primary"
          onClick={() => onDismissClick(item)}
        >
          Dismiss
        </Button>
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={() => onClassifyClick(item)}
        >
          Classify
        </Button>
      </Box>
    );
  };

  return (
    <TableRow hover sx={{ '& td': { verticalAlign: 'middle' } }}>
      <TableCell
        sx={{
          textAlign: 'center',
          lineHeight: 0,
        }}
      >
        <TriageExternalSystemIcon
          system={item.externalSystem}
          size="medium"
          matched={isMatched}
        />
      </TableCell>
      <TableCell>
        <Typography variant="body2">{item.sku || '-'}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{item.itemName}</Typography>
      </TableCell>
      <TableCell>
        <FormattedTimeValue timestamp={item.createdAt} />
      </TableCell>
      <TableCell>
        {item.triagedAt ? (
          <FormattedTimeValue timestamp={item.triagedAt} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {'\u2014'}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={statusChip.label}
          color={statusChip.color}
          size="small"
          variant="filled"
        />
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color={getItemTypeLabel() ? 'text.primary' : 'text.disabled'}
        >
          {getItemTypeLabel() || '\u2014'}
        </Typography>
      </TableCell>
      <TableCell align="right">{renderAction()}</TableCell>
    </TableRow>
  );
}
